import React from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'equasion-lib';
import { FiX, FiCameraOff } from 'react-icons/fi';
import ScanModeSwitch, { LaserModePanel } from '@/lib/shared-coms/ScanModeSwitch';
import useZoneScanSession from './useZoneScanSession';
import type { ZoneScanRules } from './useZoneScanSession';
import ZoneScanBanner from './ZoneScanBanner';
import type { ScannedZone } from './zoneByScanCode';
import './ZoneScanner.scss';

export { ZONE_BY_SCAN_CODE } from './zoneByScanCode';
export type { ScannedZone } from './zoneByScanCode';

interface ZoneScanOverlayProps extends ZoneScanRules {
  /** The X: cancel, leaving the zone as it was. Only ever the operator's choice, never a scan's. */
  onClose: () => void;
  /** A zone was accepted, after the brief confirmation. The caller closes the scanner. */
  onSelect: (zone: ScannedZone) => void;
  /**
   * The footer button: switch to the zone list. Left unset, the footer button closes the scanner
   * like the X — for screens where it means something else in the same spot ("Never mind — start
   * from the beginning", "Skip — pick without confirming the zone").
   */
  onManualEntry?: () => void;
  /** What the zone is for ("Scan destination zone"). The overlay hides the screen behind it. */
  title?: string;
  /** Which item it applies to, and which zone to scan ("Serial 123 — scan the zone it moves into"). */
  hint?: string;
  /** Copy for the footer button. */
  manualEntryLabel?: string;
}

/** Full-screen zone scanner. Mounting it claims the scanner; unmounting hands it back. */
const ZoneScanOverlay = ({
  onClose,
  onSelect,
  onManualEntry,
  title = 'Scan Zone',
  hint,
  manualEntryLabel = 'Enter zone manually',
  ...rules
}: ZoneScanOverlayProps) => {
  const {
    targetRef,
    status,
    laserMode,
    cameraMessage,
    hardwareDetected,
    rejectionFor,
    acceptZone,
    scanAgain
  } = useZoneScanSession({ ...rules, onAccepted: onSelect });

  return createPortal(
    <div className="zone-scan" role="dialog" aria-modal="true" aria-label={title}>
      <div className="zone-scan__header">
        <div className="zone-scan__titles">
          <h2>{title}</h2>
          {!!hint && <p>{hint}</p>}
        </div>
        <button
          type="button"
          className="zone-scan__close"
          onClick={onClose}
          aria-label="Close zone scanner"
        >
          <FiX />
        </button>
      </div>

      <div className="zone-scan__viewport">
        {/* Camera mode only. In laser mode nothing has asked for the camera, so a prompt to
            allow it would be telling the operator to fix something that is not broken. */}
        {!laserMode && (
          <div className="zone-scan__no-camera">
            <FiCameraOff />
            <span>{cameraMessage(!!onManualEntry)}</span>
          </div>
        )}
        {/* Stays mounted in both modes — this element is what claims the scanner for the
            overlay, in laser mode as much as in camera mode. */}
        <div className="zone-scan__camera" ref={targetRef} hidden={laserMode} />
        {laserMode && (
          <LaserModePanel
            detected={hardwareDetected}
            hint="Pull the trigger to scan the rack label"
          />
        )}
        <ScanModeSwitch className="zone-scan__mode-switch" />
        {status.kind === 'scanning' && !laserMode && (
          <div className="zone-scan__prompt">
            {hardwareDetected
              ? 'Point the camera at the zone label, or pull the handheld trigger'
              : 'Point the camera at the zone label'}
          </div>
        )}
        <ZoneScanBanner
          status={status}
          rejectionFor={rejectionFor}
          onChoose={(zone) => acceptZone(zone)}
          onScanAgain={scanAgain}
        />
      </div>

      {!!rules.allowedZones?.length && (
        <div className="zone-scan__allowed">
          <strong>Zones holding this product</strong>
          <span>{rules.allowedZones.map(({ name }) => name).join(', ')}</span>
        </div>
      )}

      <div className="zone-scan__footer">
        <Button type="secondary" fullWidth onClick={onManualEntry || onClose}>
          {manualEntryLabel}
        </Button>
      </div>
    </div>,
    document.body
  );
};

export default ZoneScanOverlay;
